import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Zap, AlertTriangle, Filter, Calendar, Download } from 'lucide-react';
import { cn, exportToCSV, SEVERITY_LABELS } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEV_COLORS = ['bg-gray-200 text-gray-700', 'bg-blue-100 text-blue-700', 'bg-yellow-100 text-yellow-700', 'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700', 'bg-red-600 text-white'];
const SEV_LABELS = ['N/C', 'Info', 'Warning', 'Average', 'High', 'Disaster'];

export function TopTriggersPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [period, setPeriod] = useState('30');
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/top-triggers?limit=100&days=${period}`, { headers: getAuthHeader() });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching top triggers:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const triggers = data?.triggers || [];
  const totalIncidentsAll = data?.total_incidents || 0;
  const periodDays = data?.period_days || 30;
  
  // Get unique categories for filter
  const categories = [...new Set(triggers.map(t => t.category))].sort();
  
  // Apply filters
  const filteredTriggers = triggers.filter(t => {
    const matchesSearch = searchTerm === '' || 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.host_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.host_groups.some(g => g.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSeverity = severityFilter === 'all' || t.severity === parseInt(severityFilter);
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    
    return matchesSearch && matchesSeverity && matchesCategory;
  });

  // Summary stats
  const totalIncidents = filteredTriggers.reduce((sum, t) => sum + t.incident_count, 0);
  const bySeverity = filteredTriggers.reduce((acc, t) => {
    acc[t.severity] = (acc[t.severity] || 0) + t.incident_count;
    return acc;
  }, {});

  const getPeriodLabel = () => {
    switch(period) {
      case '7': return 'últimos 7 dias';
      case '30': return 'últimos 30 dias';
      case '60': return 'últimos 60 dias';
      case '90': return 'últimos 90 dias';
      default: return `últimos ${period} dias`;
    }
  };

  const handleExportCSV = () => {
    const columns = [
      { key: 'description', label: 'Trigger' },
      { key: 'host_name', label: 'Host' },
      { key: 'category', label: 'Categoria' },
      { key: 'incident_count', label: 'Ocorrências' },
      { key: 'avg_duration', label: 'Duração Média' },
      { key: 'severity', label: 'Severidade' }
    ];
    
    // Map severity to label for CSV
    const dataWithSeverityLabel = filteredTriggers.map(t => ({
      ...t,
      severity: SEVERITY_LABELS[t.severity] || t.severity
    }));
    
    exportToCSV(dataWithSeverityLabel, columns, `top_triggers_${period}d`);
  };

  return (
    <div className="space-y-6" data-testid="top-triggers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Top Triggers</h2>
          <p className="text-muted-foreground">As 100 triggers mais alarmadas nos {getPeriodLabel()}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="60">Últimos 60 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="bg-red-100 text-red-700 px-3 py-1">
            <Zap className="h-3 w-3 mr-1" />{totalIncidentsAll.toLocaleString()} incidentes
          </Badge>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {[5, 4, 3, 2, 1, 0].map(sev => (
          <Card key={sev} className={`${SEV_COLORS[sev].split(' ')[0]} border-0`}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{bySeverity[sev] || 0}</p>
              <p className="text-xs">{SEV_LABELS[sev]}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por trigger, host ou grupo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Severidade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="5">Disaster</SelectItem>
              <SelectItem value="4">High</SelectItem>
              <SelectItem value="3">Average</SelectItem>
              <SelectItem value="2">Warning</SelectItem>
              <SelectItem value="1">Information</SelectItem>
              <SelectItem value="0">Not Classified</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-center">Severidade</TableHead>
                  <TableHead className="text-center">Incidentes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTriggers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                      <Zap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      Nenhuma trigger encontrada
                    </TableCell>
                  </TableRow>
                )}
                {filteredTriggers.map((trigger, index) => (
                  <TableRow key={trigger.id} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-muted-foreground">{index + 1}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{trigger.host_name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-sm truncate" title={trigger.name}>{trigger.name}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {trigger.host_groups.slice(0, 2).map((group) => (
                          <Badge key={group} variant="outline" className="text-xs">{group}</Badge>
                        ))}
                        {trigger.host_groups.length > 2 && (
                          <Badge variant="outline" className="text-xs">+{trigger.host_groups.length - 2}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">{trigger.category}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={SEV_COLORS[trigger.severity]}>{SEV_LABELS[trigger.severity]}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-bold text-lg">{trigger.incident_count}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
